import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  BdResource,
  ShareableType,
  BdResources,
} from '@bindoc-projects/resource-domain-lib';
import {
  BdResourceAbstractSelectInline,
  BdResourceSelectInlineOption,
} from '../abstract-inline-select';
import {
  BD_RESOURCES_SELECT_INLINE_CONFIG_DEFAULT,
  BdResourcesSelectInlineConfig,
} from '../inline-options';
import { SelectChangeEvent } from 'primeng/select';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BdResourceServices } from '../../../resources/service/resource.service';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { firstValueFrom, of } from 'rxjs';
import { OverlayOptions } from 'primeng/api';
import { BD_RESOURCES_DEFAULT_SORTING } from '../../../core/sorting.const';
import { BdDataTestDirective } from '@bindoc-projects/util-lib/ui';
import { IftaLabel } from 'primeng/iftalabel';
import { Select } from 'primeng/select';

@Component({
  selector: 'bd-resources-select-inline-single',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <p-iftalabel>
      <p-select
        [id]="
          'bd-resource-select-inline-single-' + resolvedConfig.resourceNameTitle
        "
        [data-test]="
          'bd-resource-select-inline-single-' + resolvedConfig.resourceNameTitle
        "
        [options]="resolvedResourceOptions"
        optionDisabled="disabled"
        optionLabel="resource.name"
        dataKey="id"
        [class]="
          optionsLoadFailed
            ? 'w-full ng-invalid ng-dirty'
            : unavailableHint
              ? 'w-full'
              : 'w-full mb-4'
        "
        [overlayOptions]="overlayOptions"
        [inputId]="
          'bd-resource-select-inline-single-' + resolvedConfig.resourceNameTitle
        "
        [formControl]="resourceCtrl"
        [showClear]="resolvedConfig?.resetOption === true || false"
        [loading]="formLoading"
        [filter]="showFilter"
        [filterPlaceholder]="resolvedConfig.resourceNameTitle"
        [placeholder]="resolvedConfig.resourceNameTitle"
        [emptyMessage]="resolvedConfig.emptyResourcesText"
        [emptyFilterMessage]="resolvedConfig.emptyResourcesText"
        variant="filled"
        (onChange)="onChange($event)"
        (onClear)="onClearChange()"
      >
        <ng-template #selectedItem>
          @if (currentResourceSelect && currentResourceSelect.resource) {
            <div class="flex align-items-center gap-2">
              <span [class]="currentResourceSelect.styleClass">{{
                currentResourceSelect.resource.name
              }}</span>
            </div>
          }
        </ng-template>
        <ng-template let-option #item>
          <div class="flex align-items-center gap-2">
            <span [class]="option.styleClass">{{ option.resource.name }}</span>
          </div>
        </ng-template>
      </p-select>
      <label
        [for]="
          'bd-resource-select-inline-single-' + resolvedConfig.resourceNameTitle
        "
        >{{ resolvedConfig.resourceNameTitle }}</label
      >
      @if (optionsLoadFailed) {
        <small class="bd-resource-select-error">
          <i class="pi pi-exclamation-triangle"></i>
          Laden fehlgeschlagen
        </small>
      } @else if (unavailableHint) {
        <small class="bd-resource-select-hint">
          <i class="pi pi-info-circle"></i>
          {{ unavailableHint }}
        </small>
      }
    </p-iftalabel>
  `,
  styles: [
    '.bd-resource-select-error, .bd-resource-select-hint { display: inline-flex; align-items: center; gap: 0.25rem; font-size: 0.75rem; }',
    '.bd-resource-select-error { color: var(--p-form-field-invalid-placeholder-color); }',
    '.bd-resource-select-hint { color: var(--p-text-muted-color); }',
  ],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    IftaLabel,
    Select,
    BdDataTestDirective,
  ],
})
export class BdResourcesSelectInlineSingleComponent<
  TResource extends BdResource | null,
> extends BdResourceAbstractSelectInline<
  TResource,
  BdResourceSelectInlineOption<TResource>
> {
  public readonly resourceOptions = input<
    BdResourceSelectInlineOption<TResource>[] | undefined
  >(undefined);
  public readonly config = input<BdResourcesSelectInlineConfig<TResource>>(
    BD_RESOURCES_SELECT_INLINE_CONFIG_DEFAULT,
  );

  readonly resourceSelect =
    input<BdResourceSelectInlineOption<TResource> | null>(null);
  readonly resourceName = input<ShareableType>();

  readonly resourceSelectChange =
    output<BdResourceSelectInlineOption<TResource> | null>();

  public readonly overlayOptions: OverlayOptions = {
    mode: 'overlay',
    appendTo: 'body',
  };

  public currentResourceSelect: BdResourceSelectInlineOption<TResource> | null =
    null;
  public showFilter = false;
  public unavailableHint: string | null = null;
  private static readonly FILTER_THRESHOLD = 3;

  private readonly _cdr = inject(ChangeDetectorRef);

  constructor() {
    const resourceServices = inject(BdResourceServices);

    super(resourceServices);

    effect(() => {
      this.resolvedConfig = this.config();
    });

    effect(() => {
      const options = this.resourceOptions();
      if (options !== undefined) {
        this.resolvedResourceOptions = options
          .filter((option) => this.resolvedConfig.filterFnc(option.resource))
          .sort((a, b) =>
            BdResources.sort(
              a.resource,
              b.resource,
              BD_RESOURCES_DEFAULT_SORTING,
            ),
          );
        // Options supplied by the parent arrive asynchronously, long after the
        // initial (usually empty) binding. Without this the filter threshold
        // would stay frozen on that first value and the search box could never
        // appear.
        this._resourceOptionsLoaded$.next(this.resolvedResourceOptions);
      }
    });

    this.resourceCtrl.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((resources) => {
        this._onSelect(resources);
      });

    this._resourceOptionsLoaded$
      .asObservable()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((options) => {
        this.showFilter =
          options.length >
          BdResourcesSelectInlineSingleComponent.FILTER_THRESHOLD;
        this._cdr.markForCheck();
      });

    effect(() => {
      const select = this.resourceSelect();
      if (select) {
        this._onResourceSelectChange(select);
      }
    });

    effect(() => {
      const name = this.resourceName();
      if (name) {
        this._onResourceNameChange(name);
      }
    });
  }

  public onChange(change: SelectChangeEvent): void {
    this._onSelect(change.value);
  }

  public onClearChange(): void {
    this._onSelect(null);
  }

  protected _onSelect(
    resourceSelect: BdResourceSelectInlineOption<TResource> | null,
  ): void {
    this.currentResourceSelect = resourceSelect;
    this.resourceSelectChange.emit(resourceSelect);
  }

  private _onResourceSelectChange(
    resourceSelect: BdResourceSelectInlineOption<TResource>,
  ): void {
    this.updateResourceSelectResolvingInProgress(true);
    let req: Promise<BdResourceSelectInlineOption<TResource>>;

    if (resourceSelect.resource) {
      req = Promise.resolve(resourceSelect);
    } else {
      req = firstValueFrom(
        this._resourceOptionsLoaded$.asObservable().pipe(
          switchMap((options) => {
            if (options.some((option) => option.id === resourceSelect.id)) {
              return of(
                options.find((option) => option.id === resourceSelect.id),
              );
            } else {
              return this._resourceService.findById(resourceSelect.id).pipe(
                take(1),
                map((resource) =>
                  this.resourceAsAdminInlineOption(resource as TResource),
                ),
                tap((option) => this.addOption(option)),
              );
            }
          }),
        ),
      );
    }

    req
      .then((resolved) => {
        this.updateResourceSelectResolvingInProgress(false);
        this.currentResourceSelect = resolved;
        this.unavailableHint = this.buildUnavailableSelectionHint([resolved]);
        this.resourceCtrl.setValue(resolved);
        this._cdr.markForCheck();
      })
      .catch((err) => {
        this.updateResourceSelectResolvingInProgress(false);
        const placeholder = this.resolveExpectedErrorPlaceholder(
          err,
          resourceSelect.id,
        );
        if (placeholder) {
          this.addOption(placeholder);
          this.currentResourceSelect = placeholder;
          this.unavailableHint = this.buildUnavailableSelectionHint([
            placeholder,
          ]);
          this.resourceCtrl.setValue(placeholder);
        } else {
          this.optionsLoadFailed = true;
        }
        this._cdr.markForCheck();
      });
  }
}
